import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { calculateYBands } from "@/lib/utils/sensitivity";
import { ImageAnalysisProperties, ImageAnalytics } from "@/types/imagery";
import { UseQueryResult } from "@tanstack/react-query";
import { Feature, MultiPolygon } from "geojson";
import { useCallback, useEffect, useMemo } from "react";
import {
    Bar,
    BarChart,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    CartesianGrid,
    ErrorBar,
} from "recharts";
import * as turf from "@turf/turf";
import { useFormContext } from "react-hook-form";
import { roundTo } from "@/lib/utils/numbers";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { Spinner } from "@heroui/react";
import { FillLayerSpecification, SymbolLayerSpecification } from "mapbox-gl";
import { LineLayerSpecification } from "mapbox-gl";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeSourcesIfExists,
    removeLayersIfExists,
    moveLayersToTop,
} from "@/lib/utils/map";
import { CategoricalChartFunc } from "recharts/types/chart/generateCategoricalChart";
import { useTranslation } from "react-i18next";
const GRAPH_SOURCE = "trendline-graph-source";
const FILL_LAYER: FillLayerSpecification = {
    source: GRAPH_SOURCE,
    id: "bar-graph-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    layout: {},
    paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 0.8,
    },
};
const LINE_LAYER: LineLayerSpecification = {
    source: GRAPH_SOURCE,
    id: "bar-graph-line",
    type: "line",
    layout: {},
    paint: {
        "line-color": ["get", "color"],
        "line-width": 3,
    },
    minzoom: 14,
};
const SYMBOL_LAYER: SymbolLayerSpecification = {
    source: GRAPH_SOURCE,
    id: "bar-graph-symbol",
    type: "symbol",
    layout: {
        "text-field": ["get", "pair_id"],
        "text-size": 12,
    },
};

function SIBar({
    imageAnalyticsQuery,
}: {
    imageAnalyticsQuery: UseQueryResult<ImageAnalytics>;
}) {
    const { t } = useTranslation("insights");
    const { fieldQuery } = useFieldDashboard() ?? {};
    const f = useFormContext();
    const pairIds = f.watch("pairIds");

    const bands = useMemo(
        () =>
            calculateYBands(
                fieldQuery?.data?.insight_sensitivity ?? "moderate",
            ),
        [fieldQuery?.data?.insight_sensitivity],
    );

    const getBandColor = useCallback(
        (si: number) => {
            return (
                sortArrayByKey(bands, "y1", "asc").find((band) => si <= band.y1)
                    ?.color ?? "gray"
            );
        },
        [bands],
    );
    const data = useMemo(() => {
        const groupedFeatures =
            imageAnalyticsQuery.data?.features
                ?.filter((f) => f.properties.plot_type == "Canary")
                .reduce(
                    (acc, feature) => {
                        const pairId = feature.properties.pair_id;
                        if (!pairId) return acc;
                        if (!acc[pairId]) {
                            acc[pairId] = [feature];
                        } else {
                            acc[pairId].push(feature);
                        }

                        return acc;
                    },
                    {} as Record<
                        number,
                        Feature<MultiPolygon, ImageAnalysisProperties>[]
                    >,
                ) ?? {};

        const weightedAvgData = Object.keys(groupedFeatures).map((pairId) => {
            const features = groupedFeatures[Number(pairId)];

            const totalArea = features.reduce((sum, feature) => {
                return feature.properties?.sensitized_si
                    ? sum + turf.area(feature)
                    : sum;
            }, 0);

            const weightedSum = features.reduce((sum, feature) => {
                const area = turf.area(feature);
                return feature.properties?.sensitized_si
                    ? sum + feature.properties.sensitized_si * area
                    : sum;
            }, 0);

            const areaWeightedAvg = (weightedSum / totalArea) * 100;
            const minSi =
                Math.min(
                    ...features.map((f) => f.properties.sensitized_si ?? 100),
                ) * 100;
            const maxSi =
                Math.max(
                    ...features.map((f) => f.properties.sensitized_si ?? 0),
                ) * 100;

            return {
                pair_id: parseInt(pairId),
                sensitized_si: areaWeightedAvg,
                plot_id: features[0].properties.plot,
                error: [areaWeightedAvg - minSi, maxSi - areaWeightedAvg],
            };
        });

        return (
            weightedAvgData
                .sort((a, b) => a.pair_id - b.pair_id)
                .map((feature) => ({
                    pair_id: feature.pair_id,
                    sensitized_si: feature.sensitized_si
                        ? feature.sensitized_si
                        : null,
                    color: getBandColor(feature.sensitized_si ?? 0),
                    plot_id: feature.plot_id,
                    error: feature.error,
                }))
                .filter((row) => {
                    if (!pairIds?.length) return row;
                    return pairIds.includes(row.pair_id.toString());
                }) ?? []
        );
    }, [getBandColor, imageAnalyticsQuery.data?.features, pairIds]);

    const [min, max] = useMemo(() => {
        return [
            roundTo(
                Math.min(
                    ...data
                        .filter((d) => !!d.sensitized_si)
                        .map((d) => d.sensitized_si ?? 0),
                ) - 2,
                0,
            ),
            roundTo(
                Math.max(
                    ...data
                        .filter((d) => !!d.sensitized_si)
                        .map((d) => d.sensitized_si ?? 100),
                ) + 2,
                0,
            ),
        ];
    }, [data]);

    const ticks = useMemo(() => {
        return Array.from({ length: (110 - 0) / 5 + 1 }, (_, i) =>
            roundTo(0 + i * 5, 0),
        ).filter((t) => t <= max && t >= min);
    }, [max, min]);

    const { map, mapLoaded } = useMap();

    useEffect(() => {
        if (!map || !mapLoaded) return;

        addUpdateGeojsonSource(map, GRAPH_SOURCE, {
            type: "geojson",
            tolerance: 0,
            data: {
                type: "FeatureCollection",
                features: [],
            },
        });
        addUpdateGeojsonLayers(map, [FILL_LAYER, LINE_LAYER, SYMBOL_LAYER]);

        return () => {
            if (map) {
                removeLayersIfExists(map, [
                    FILL_LAYER.id,
                    LINE_LAYER.id,
                    SYMBOL_LAYER.id,
                ]);
                removeSourcesIfExists(map, [GRAPH_SOURCE]);
            }
        };
    }, [map, mapLoaded]);

    const onEnter: CategoricalChartFunc = useCallback(
        (data) => {
            if (!map || !mapLoaded) return;

            const payload = data.activePayload?.[0]?.payload;
            const plotId = payload?.plot_id;
            if (!plotId) return;

            const features =
                imageAnalyticsQuery.data?.features
                    ?.filter((f) => f.properties.plot == plotId)
                    .map((f) => {
                        const color =
                            bands?.find((band) => {
                                return (
                                    f.properties?.sensitized_si != null &&
                                    f.properties.sensitized_si * 100 >=
                                        band.y0 &&
                                    f.properties.sensitized_si * 100 < band.y1
                                );
                            })?.mapColor || "gray";
                        return { ...f, properties: { ...f.properties, color } };
                    }) ?? [];

            addUpdateGeojsonSource(map, GRAPH_SOURCE, {
                type: "geojson",
                tolerance: 0,
                data: {
                    type: "FeatureCollection",
                    features,
                },
            });
            moveLayersToTop(map, [
                FILL_LAYER.id,
                LINE_LAYER.id,
                SYMBOL_LAYER.id,
            ]);
        },
        [imageAnalyticsQuery.data?.features, map, mapLoaded, bands],
    );

    const onLeave = useCallback(() => {
        if (!map || !mapLoaded) return;

        addUpdateGeojsonSource(map, GRAPH_SOURCE, {
            type: "geojson",
            tolerance: 0,
            data: {
                type: "FeatureCollection",
                features: [],
            },
        });
    }, [map, mapLoaded]);
    return (
        <ResponsiveContainer width="100%" height={400}>
            {imageAnalyticsQuery.isLoading ? (
                <div className="flex justify-center items-center h-full">
                    <Spinner label={t("messages.loading")} />
                </div>
            ) : !data.length ? (
                <div className="bg-gray-100 h-full rounded-sm">
                    <div className=" p-3 italic text-center">
                        {t("messages.noDataSelectedImage")}
                    </div>
                </div>
            ) : (
                <BarChart
                    data={data}
                    margin={{ bottom: 16, left: 8 }}
                    onMouseMove={onEnter}
                    onMouseLeave={onLeave}
                >
                    <CartesianGrid vertical={false} />
                    <XAxis
                        dataKey="pair_id"
                        label={{ value: t("chartLabels.pairIdHash") }}
                    />
                    <YAxis
                        label={{
                            value: t("tableColumns.siPercent"),
                            angle: -90,
                            position: "left",
                            dx: 10,
                        }}
                        tickFormatter={(t) => `${t}%`}
                        domain={[min, max]}
                        ticks={ticks}
                    />
                    <Tooltip
                        labelFormatter={(l) =>
                            `${t("chartLabels.pairIdHash")}${l}`
                        }
                        formatter={(value) => {
                            return [`${roundTo(value as number, 1)}%`];
                        }}
                    />

                    <Bar dataKey="sensitized_si">
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                        <ErrorBar
                            dataKey="error"
                            direction="y"
                            strokeWidth={2}
                            stroke="gray"
                        />
                    </Bar>
                </BarChart>
            )}
        </ResponsiveContainer>
    );
}

export default SIBar;
